import type { Address } from "viem"
import {
  type TransactionItem,
  type TransactionItemUnion,
  TransactionLabel,
  TransactionSubLabel,
} from "./baseTransactionTypes"
import { SwapperMode } from "./swapTransactionTypes"

export type CompoundTransactionItem = {
  label:
    | TransactionLabel.LEVERAGE
    | TransactionLabel.CREATE_DEBT
    | TransactionLabel.VAULT_SWAP
    | TransactionLabel.VAULT_SWAP_REPAY
    | TransactionLabel.VAULT_SWAP_DEBT
    | TransactionLabel.CLAIM_REWARDS
  from: Address
  to: Address
  subLabel?: string
  slippage?: number
  transactions: TransactionItem[]
}

export function isCompoundTransaction(
  transaction?: TransactionItemUnion,
): transaction is CompoundTransactionItem {
  return (
    transaction?.label === TransactionLabel.LEVERAGE ||
    transaction?.label === TransactionLabel.CREATE_DEBT ||
    transaction?.label === TransactionLabel.VAULT_SWAP_REPAY ||
    transaction?.label === TransactionLabel.VAULT_SWAP ||
    transaction?.label === TransactionLabel.VAULT_SWAP_DEBT ||
    transaction?.label === TransactionLabel.CLAIM_REWARDS
  )
}

export type LeverageTransactionItem = CompoundTransactionItem & {
  label: TransactionLabel.LEVERAGE
  leverage?: number
  maxLeverage?: number
  minLeverage?: number
  collateralVault: Address
  liabilityVault: Address
  longVault: Address
  isEdit?: boolean
  isWrap?: boolean
}

export type EditMultipleTransactionItem = LeverageTransactionItem & {
  isEdit: true
}

export function isLeverageTransaction(
  transaction?: TransactionItemUnion,
): transaction is LeverageTransactionItem {
  return transaction?.label === TransactionLabel.LEVERAGE
}

export function isEditMultipleTransaction(
  transaction?: TransactionItemUnion,
): transaction is EditMultipleTransactionItem {
  return Boolean(
    transaction?.label === TransactionLabel.LEVERAGE &&
      "isEdit" in transaction &&
      transaction.isEdit,
  )
}

export type CreateDebtTransactionItem = CompoundTransactionItem & {
  label: TransactionLabel.CREATE_DEBT
  collateralVault: Address
  liabilityVault: Address
  ltv?: number
  isWrap?: boolean
}

export function isCreateDebtTransaction(
  transaction?: TransactionItemUnion,
): transaction is CreateDebtTransactionItem {
  return transaction?.label === TransactionLabel.CREATE_DEBT
}

type ExtraVaultSwapDebtTransactionItem = {
  label: TransactionLabel.VAULT_SWAP_DEBT
  subLabel: TransactionSubLabel.REPAY_WITH_SHARES_SWAP_EXACT_OUT
  type: RepayType
  fromVault: Address
  fromAmount: string
  toVault: Address
  isMax?: boolean
  amountIn: Address
}

export type VaultSwapDebtTransactionItem = CompoundTransactionItem &
  ExtraVaultSwapDebtTransactionItem

export type VaultSwapTransactionItem = CompoundTransactionItem & {
  label: TransactionLabel.VAULT_SWAP
  fromVault: Address
  toVault: Address
  swapperMode: SwapperMode
  fromAmount: string
  toAmount: string
  isMax?: boolean
  isSavings?: boolean
}

export function isVaultSwapDebtTransaction(
  transaction?: TransactionItemUnion,
): transaction is VaultSwapDebtTransactionItem {
  return transaction?.label === TransactionLabel.VAULT_SWAP_DEBT
}
export function isVaultSwapTransaction(
  transaction?: TransactionItemUnion,
): transaction is VaultSwapTransactionItem {
  return transaction?.label === TransactionLabel.VAULT_SWAP
}

export enum RepayType {
  WALLET = "fromWallet",
  COLLATERAL = "fromCollateral",
  SAVINGS = "fromSavings",
}

export type VaultSwapRepayTransactionItem = CompoundTransactionItem & {
  label: TransactionLabel.VAULT_SWAP_REPAY
  subLabel: string
  fromVault: Address
  toVault: Address
  /// deprecated - use `tsx.type === RepayType.WALLET` instead
  isFromWallet?: boolean
  type: RepayType
  isMax?: boolean
  isWrap?: boolean
}

export function isVaultSwapRepayTransaction(
  transaction?: TransactionItemUnion,
): transaction is VaultSwapRepayTransactionItem {
  return transaction?.label === TransactionLabel.VAULT_SWAP_REPAY
}

type ClaimRewardsTransactionItem = CompoundTransactionItem & {
  label: TransactionLabel.CLAIM_REWARDS
}

export function isClaimRewardsTransaction(
  transaction?: TransactionItemUnion,
): transaction is ClaimRewardsTransactionItem {
  return transaction?.label === TransactionLabel.CLAIM_REWARDS
}

export type CompoundTransactionUnion =
  | LeverageTransactionItem
  | EditMultipleTransactionItem
  | CreateDebtTransactionItem
  | VaultSwapTransactionItem
  | VaultSwapDebtTransactionItem
  | VaultSwapRepayTransactionItem
  | ClaimRewardsTransactionItem
